// DeepSecurityTab.js - Deep Security scan results display
// Shows per-tool breakdowns (Nuclei DAST, TestSSL, Nikto, Nmap Vuln)
// PRD-36: Sorting, Filtering, Search, View Mode, Finding Explanations

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getAssetScanDetails } from '../../services/assetService';
import FindingFilterBar from './FindingFilterBar';
import FINDING_EXPLANATIONS from '../../data/findingExplanations';
import './DeepSecurityTab.css';

const SEVERITY_WEIGHT = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

const TOOL_CHIP_COLORS = {
    'nuclei-injection': { label: 'Nuclei', color: '#00D9FF' },
    'testssl': { label: 'TestSSL', color: '#FFB800' },
    'nikto': { label: 'Nikto', color: '#39FF14' },
    'nmap-vuln': { label: 'Nmap', color: '#6B7FFF' }
};

// Determine tool source for a finding
const getToolSource = (finding) => {
    if (finding.source && TOOL_CHIP_COLORS[finding.source]) return finding.source;
    const tid = finding.templateId || '';
    if (tid.startsWith('dast/') || tid.startsWith('http/')) return 'nuclei-injection';
    if (tid.startsWith('testssl/')) return 'testssl';
    if (tid.startsWith('nikto/')) return 'nikto';
    return 'nuclei-injection';
};

const DeepSecurityTab = ({ asset, assetId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedSections, setExpandedSections] = useState({
        nuclei: true, testssl: true, nikto: true, nmapVuln: false
    });

    // PRD-36 state
    const [activeFilters, setActiveFilters] = useState(null); // null = not yet initialized
    const [sortBy, setSortBy] = useState('severity');
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState('tool');
    const [allExpanded, setAllExpanded] = useState(null); // null=individual, true=all expanded, false=all collapsed

    const loadData = useCallback(async () => {
        if (!asset?.lastDeepSecurityResultId || !assetId) return;
        setLoading(true);
        setError(null);
        try {
            const result = await getAssetScanDetails(assetId, asset.lastDeepSecurityResultId);
            setData(result);
        } catch (err) {
            console.error('Failed to load deep security data:', err);
            setError(err.message || 'Failed to load deep security results');
        } finally {
            setLoading(false);
        }
    }, [assetId, asset?.lastDeepSecurityResultId]);

    useEffect(() => { loadData(); }, [loadData]);

    const fullResult = data?.fullResult || {};
    const findings = useMemo(() => fullResult.findings || [], [fullResult.findings]);
    const summary = fullResult.summary || {};
    const scanMetadata = fullResult.scanMetadata || {};

    // Initialize default filters based on finding count (PRD-36 §4.2)
    useEffect(() => {
        if (findings.length > 0 && activeFilters === null) {
            if (findings.length > 50) {
                setActiveFilters(new Set(['critical', 'high', 'medium']));
            } else {
                setActiveFilters(new Set(['critical', 'high', 'medium', 'low', 'info']));
            }
        }
    }, [findings.length, activeFilters]);

    // Effective filters (show all if not initialized yet)
    const ALL_SEVERITIES = useMemo(() => new Set(['critical', 'high', 'medium', 'low', 'info']), []);
    const effectiveFilters = activeFilters || ALL_SEVERITIES;

    // Filter + sort findings (PRD-36 §4.1)
    const processedFindings = useMemo(() => {
        let result = findings;

        // Severity filter
        result = result.filter(f => effectiveFilters.has((f.severity || 'info').toLowerCase()));

        // Search filter
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter(f => {
                const searchFields = [
                    f.name, f.description, f.host, f.matchedAt, f.templateId,
                    (f.tags || []).join(' '), (f.cves || []).join(' ')
                ].filter(Boolean).join(' ').toLowerCase();
                return searchFields.includes(q);
            });
        }

        // Sort
        result = [...result].sort((a, b) => {
            if (sortBy === 'severity') {
                const diff = (SEVERITY_WEIGHT[a.severity] || 4) - (SEVERITY_WEIGHT[b.severity] || 4);
                if (diff !== 0) return diff;
                return (a.host || '').localeCompare(b.host || '');
            }
            if (sortBy === 'host') return (a.host || '').localeCompare(b.host || '');
            if (sortBy === 'template') return (a.templateId || '').localeCompare(b.templateId || '');
            if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
            return 0;
        });

        return result;
    }, [findings, effectiveFilters, searchQuery, sortBy]);

    // Group processed findings by tool (for "By Tool" view)
    const groupedFindings = useMemo(() => {
        const groups = { 'nuclei-injection': [], 'testssl': [], 'nikto': [], 'nmap-vuln': [] };
        processedFindings.forEach(f => {
            const source = getToolSource(f);
            groups[source].push(f);
        });
        return groups;
    }, [processedFindings]);

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const handleToggleFilter = (severity) => {
        setActiveFilters(prev => {
            const next = new Set(prev || ['critical', 'high', 'medium', 'low', 'info']);
            if (next.has(severity)) next.delete(severity);
            else next.add(severity);
            return next;
        });
    };

    const handleShowAll = () => {
        setActiveFilters(new Set(['critical', 'high', 'medium', 'low', 'info']));
    };

    if (loading) {
        return <div className="ds-loading">Loading deep security results...</div>;
    }

    if (!asset?.lastDeepSecurityResultId) {
        return (
            <div className="ds-empty-state">
                <h3>No Deep Security Scan Yet</h3>
                <p>Deep security scans run weekly on Sundays for Pro+ tier assets.</p>
                {asset?.nextDeepSecurityAt && (
                    <p className="ds-next-scan">
                        Next scan scheduled: {new Date(asset.nextDeepSecurityAt).toLocaleDateString()}
                    </p>
                )}
            </div>
        );
    }

    if (error) {
        return (
            <div className="ds-empty-state">
                <p>Unable to load deep security scan results.</p>
                <p className="ds-error-msg">{error}</p>
                <button className="ds-retry-btn" onClick={loadData}>Retry</button>
            </div>
        );
    }

    if (!data) {
        return <div className="ds-loading">Loading deep security results...</div>;
    }

    const totalFindings = findings.length;
    const scanDuration = fullResult.scanDuration
        ? `${Math.floor(fullResult.scanDuration / 60)}m ${fullResult.scanDuration % 60}s`
        : 'Unknown';

    return (
        <div className="ds-tab">
            {/* Scan Metadata Header */}
            <div className="ds-metadata">
                <div className="ds-meta-grid">
                    <div className="ds-meta-item">
                        <span className="ds-meta-label">Last Scan</span>
                        <span className="ds-meta-value">
                            {fullResult.completedAt
                                ? new Date(fullResult.completedAt).toLocaleString()
                                : asset.lastDeepSecurityAt
                                    ? new Date(asset.lastDeepSecurityAt).toLocaleString()
                                    : 'N/A'}
                        </span>
                    </div>
                    <div className="ds-meta-item">
                        <span className="ds-meta-label">Duration</span>
                        <span className="ds-meta-value">{scanDuration}</span>
                    </div>
                    <div className="ds-meta-item">
                        <span className="ds-meta-label">Tools Used</span>
                        <span className="ds-meta-value">
                            {(scanMetadata.templatesUsed || []).join(', ') || 'N/A'}
                        </span>
                    </div>
                    <div className="ds-meta-item">
                        <span className="ds-meta-label">WAF Status</span>
                        <span className={`ds-meta-value ${summary.wafDetected ? 'ds-waf-detected' : ''}`}>
                            {summary.wafDetected
                                ? `Detected (${summary.wafType || 'Unknown'})`
                                : 'Not Detected'}
                        </span>
                    </div>
                </div>
            </div>

            {/* PRD-36: Interactive Filter Bar (replaces static summary badges) */}
            {totalFindings > 0 ? (
                <>
                    <FindingFilterBar
                        summary={summary}
                        totalFindings={totalFindings}
                        filteredCount={processedFindings.length}
                        activeFilters={effectiveFilters}
                        onToggleFilter={handleToggleFilter}
                        onShowAll={handleShowAll}
                        sortBy={sortBy}
                        onSortChange={setSortBy}
                        searchQuery={searchQuery}
                        onSearchChange={setSearchQuery}
                        viewMode={viewMode}
                        onViewModeChange={(mode) => { setViewMode(mode); setAllExpanded(null); }}
                        allExpanded={allExpanded}
                        onExpandAll={() => setAllExpanded(true)}
                        onCollapseAll={() => setAllExpanded(false)}
                    />

                    {/* Render findings based on view mode */}
                    {viewMode === 'tool' ? (
                        <>
                            <ToolSection
                                title="Nuclei DAST"
                                findings={groupedFindings['nuclei-injection']}
                                expanded={expandedSections.nuclei}
                                onToggle={() => toggleSection('nuclei')}
                                emptyMessage="No injection/DAST findings detected."
                                allExpanded={allExpanded}
                                onCardToggle={() => setAllExpanded(null)}
                            />
                            <ToolSection
                                title="TestSSL"
                                findings={groupedFindings['testssl']}
                                expanded={expandedSections.testssl}
                                onToggle={() => toggleSection('testssl')}
                                emptyMessage="No SSL/TLS issues found."
                                allExpanded={allExpanded}
                                onCardToggle={() => setAllExpanded(null)}
                            />
                            <ToolSection
                                title="Nikto"
                                findings={groupedFindings['nikto']}
                                expanded={expandedSections.nikto}
                                onToggle={() => toggleSection('nikto')}
                                emptyMessage="No web server misconfigurations found."
                                skipped={summary.niktoSkipped}
                                skippedMessage={`Nikto scanning was skipped due to WAF detection (${summary.wafType || 'Unknown'}). Running Nikto behind a WAF can cause false positives and IP blocking.`}
                                allExpanded={allExpanded}
                                onCardToggle={() => setAllExpanded(null)}
                            />
                            {asset.assetType !== 'domain' && (
                                <ToolSection
                                    title="Nmap Vuln Scripts"
                                    findings={groupedFindings['nmap-vuln']}
                                    expanded={expandedSections.nmapVuln}
                                    onToggle={() => toggleSection('nmapVuln')}
                                    emptyMessage="No network vulnerabilities found."
                                    allExpanded={allExpanded}
                                    onCardToggle={() => setAllExpanded(null)}
                                />
                            )}
                        </>
                    ) : (
                        /* Flat by Severity view */
                        <div className="ds-flat-list">
                            {processedFindings.length > 0 ? (
                                <div className="ds-findings-list">
                                    {processedFindings.map((finding, idx) => (
                                        <FindingCard
                                            key={idx}
                                            finding={finding}
                                            showToolChip
                                            allExpanded={allExpanded}
                                            onToggle={() => setAllExpanded(null)}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <p className="ds-section-empty">No findings match the current filters.</p>
                            )}
                        </div>
                    )}
                </>
            ) : (
                <div className="ds-no-findings">
                    <p>Deep scan completed — no additional vulnerabilities found.</p>
                    {summary.wafDetected && (
                        <p className="ds-waf-notice">
                            WAF detected ({summary.wafType}). Some testing was limited to avoid blocking.
                            {summary.niktoSkipped && ' Nikto web scanning was skipped.'}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

// Tool Section sub-component
const ToolSection = ({ title, findings, expanded, onToggle, emptyMessage, skipped, skippedMessage, allExpanded, onCardToggle }) => (
    <div className="ds-tool-section">
        <div className="ds-section-header" onClick={onToggle}>
            <h4>
                {title}
                <span className="ds-count-badge">
                    {skipped ? 'Skipped' : findings.length}
                </span>
            </h4>
            <span className="ds-toggle">{expanded ? '\u25BE' : '\u25B8'}</span>
        </div>
        {expanded && (
            <div className="ds-section-content">
                {skipped ? (
                    <p className="ds-section-empty ds-skipped">{skippedMessage}</p>
                ) : findings.length > 0 ? (
                    <div className="ds-findings-list">
                        {findings.map((finding, idx) => (
                            <FindingCard
                                key={idx}
                                finding={finding}
                                allExpanded={allExpanded}
                                onToggle={onCardToggle}
                            />
                        ))}
                    </div>
                ) : (
                    <p className="ds-section-empty">{emptyMessage}</p>
                )}
            </div>
        )}
    </div>
);

// Finding Card sub-component — PRD-36: explanations + tool chips
const FindingCard = ({ finding, showToolChip, allExpanded, onToggle }) => {
    const [localExpanded, setLocalExpanded] = useState(false);
    const expanded = allExpanded !== null ? allExpanded : localExpanded;
    const severityClass = `ds-severity-${(finding.severity || 'info').toLowerCase()}`;
    const toolSource = getToolSource(finding);
    const chipInfo = TOOL_CHIP_COLORS[toolSource];
    const explanation = FINDING_EXPLANATIONS[finding.templateId];

    const handleToggle = () => {
        setLocalExpanded(!expanded);
        if (allExpanded !== null && onToggle) onToggle();
    };

    return (
        <div className={`ds-finding-card ${severityClass}`}>
            <div className="ds-finding-header" onClick={handleToggle}>
                <span className={`ds-severity-badge ${severityClass}`}>
                    {(finding.severity || 'INFO').toUpperCase()}
                </span>
                {showToolChip && chipInfo && (
                    <span
                        className="ds-tool-chip"
                        style={{
                            color: chipInfo.color,
                            borderColor: chipInfo.color + '55',
                            background: chipInfo.color + '1A'
                        }}
                    >{chipInfo.label}</span>
                )}
                <span className="ds-finding-name">{finding.name || finding.templateId}</span>
                <span className="ds-finding-toggle">{expanded ? '\u25BE' : '\u25B8'}</span>
            </div>
            {expanded && (
                <div className="ds-finding-details">
                    {finding.description && (
                        <div className="ds-detail-row">
                            <span className="ds-detail-label">Description</span>
                            <span className="ds-detail-value">{finding.description}</span>
                        </div>
                    )}
                    {finding.matchedAt && (
                        <div className="ds-detail-row">
                            <span className="ds-detail-label">Target</span>
                            <span className="ds-detail-value">{finding.matchedAt}</span>
                        </div>
                    )}
                    {finding.host && finding.host !== finding.matchedAt && (
                        <div className="ds-detail-row">
                            <span className="ds-detail-label">Host</span>
                            <span className="ds-detail-value">{finding.host}</span>
                        </div>
                    )}
                    {finding.templateId && (
                        <div className="ds-detail-row">
                            <span className="ds-detail-label">Template</span>
                            <span className="ds-detail-value ds-mono">{finding.templateId}</span>
                        </div>
                    )}
                    {finding.tags && finding.tags.length > 0 && (
                        <div className="ds-detail-row">
                            <span className="ds-detail-label">Tags</span>
                            <div className="ds-tags">
                                {finding.tags.map((tag, i) => (
                                    <span key={i} className="ds-tag">{tag}</span>
                                ))}
                            </div>
                        </div>
                    )}
                    {finding.cves && finding.cves.length > 0 && (
                        <div className="ds-detail-row">
                            <span className="ds-detail-label">CVEs</span>
                            <div className="ds-tags">
                                {finding.cves.map((cve, i) => (
                                    <span key={i} className="ds-tag">{cve}</span>
                                ))}
                            </div>
                        </div>
                    )}
                    {finding.reference && (
                        <div className="ds-detail-row">
                            <span className="ds-detail-label">Reference</span>
                            <span className="ds-detail-value">{
                                Array.isArray(finding.reference)
                                    ? finding.reference.join(', ')
                                    : finding.reference
                            }</span>
                        </div>
                    )}

                    {/* PRD-36: Finding Explanation Section */}
                    {explanation && (
                        <div className="ds-explanation">
                            <div className="ds-explanation-header">{explanation.title}</div>
                            <div className="ds-explanation-section">
                                <span className="ds-explanation-label">What this means</span>
                                <p>{explanation.explanation}</p>
                            </div>
                            <div className="ds-explanation-section">
                                <span className="ds-explanation-label">Impact</span>
                                <p>{explanation.impact}</p>
                            </div>
                            <div className="ds-explanation-section">
                                <span className="ds-explanation-label">Remediation</span>
                                <p>{explanation.remediation}</p>
                            </div>
                            {explanation.severityGuide && (
                                <div className="ds-explanation-section">
                                    <span className="ds-explanation-label">Severity Guide</span>
                                    <div className="ds-tags">
                                        {Object.entries(explanation.severityGuide).map(([val, sev]) => (
                                            <span key={val} className={`ds-tag ds-sev-guide-${sev}`}>
                                                {val} = {sev}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DeepSecurityTab;
